import { useEffect, useMemo, useState } from 'react'
import type { TenantBootstrapModel } from '@unified-lk/tenant-config'
import { staffApi } from '../../api/staff'
import type {
  AuthSession,
  StaffPatientActivityData,
  StaffPatientCommentsData,
  StaffPatientMedicalCardsData,
  StaffPatientSegmentsData,
  StaffPatientVerificationCodesData,
  StaffPatientWorkspaceData,
  StaffPatientsListData,
} from '../../types/plans'
import { PatientActivitySection } from './PatientActivitySection'
import { StaffShell } from '../StaffShell'
import { PatientCommentsSection } from './PatientCommentsSection'
import { PatientListSection } from './PatientListSection'
import { PatientMedicalCardsSection } from './PatientMedicalCardsSection'
import { PatientSegmentsSection } from './PatientSegmentsSection'
import { PatientVerificationCodesSection } from './PatientVerificationCodesSection'
import { PatientWorkspaceDetailSection } from './PatientWorkspaceDetailSection'
import { PatientDiagnocatSection } from './PatientDiagnocatSection'
import { DiagnocatPatientCardWizard } from '../plans/constructor/diagnostics/DiagnocatPatientCardWizard'
import { PlanDiagnosticsBetaCardSection } from '../plans/constructor/diagnostics/PlanDiagnosticsBetaCardSection'
import { SecretLoginLinkDialog } from '../SecretLoginLinkDialog'

interface NavigationItem {
  key: string
  label: string
}

type PatientSectionKey = 'list' | 'medical-cards' | 'comments' | 'segments' | 'verification-codes' | 'activity'

const sectionLabels: Record<PatientSectionKey, string> = {
  list: 'Список пациентов',
  'medical-cards': 'Медкарты',
  comments: 'Комментарии',
  segments: 'Сегментация',
  'verification-codes': 'SMS коды',
  activity: 'Активность ЛК',
}

function relativeIsoDate(offsetDays: number) {
  const date = new Date()
  date.setDate(date.getDate() + offsetDays)

  return date.toISOString().slice(0, 10)
}

export function StaffPatientsWorkspace({
  bootstrap,
  navigation,
  currentView,
  onNavigate,
  session,
  onLogout,
}: {
  bootstrap: TenantBootstrapModel
  navigation: NavigationItem[]
  currentView: string
  onNavigate: (view: string) => void
  session: AuthSession
  onLogout: () => Promise<void> | void
}) {
  const [currentSection, setCurrentSection] = useState<PatientSectionKey>('list')
  const [patientsList, setPatientsList] = useState<StaffPatientsListData | null>(null)
  const [medicalCards, setMedicalCards] = useState<StaffPatientMedicalCardsData | null>(null)
  const [comments, setComments] = useState<StaffPatientCommentsData | null>(null)
  const [segments, setSegments] = useState<StaffPatientSegmentsData | null>(null)
  const [verificationCodes, setVerificationCodes] = useState<StaffPatientVerificationCodesData | null>(null)
  const [activity, setActivity] = useState<StaffPatientActivityData | null>(null)
  const [selectedPatientId, setSelectedPatientId] = useState<number | null>(null)
  const [patientWorkspace, setPatientWorkspace] = useState<StaffPatientWorkspaceData | null>(null)
  const [workspaceLoading, setWorkspaceLoading] = useState(false)
  const [workspaceError, setWorkspaceError] = useState<string | null>(null)
  const [loadingMap, setLoadingMap] = useState<Record<PatientSectionKey, boolean>>({
    list: false,
    'medical-cards': false,
    comments: false,
    segments: false,
    'verification-codes': false,
    activity: false,
  })
  const [errorMap, setErrorMap] = useState<Partial<Record<PatientSectionKey, string>>>({})
  const [searchInput, setSearchInput] = useState('')
  const [appliedSearch, setAppliedSearch] = useState('')
  const [page, setPage] = useState(1)
  const [medicalCardsDraft, setMedicalCardsDraft] = useState({
    fromDate: relativeIsoDate(-30),
    toDate: relativeIsoDate(0),
    doctorId: '',
  })
  const [medicalCardsQuery, setMedicalCardsQuery] = useState({
    fromDate: relativeIsoDate(-30),
    toDate: relativeIsoDate(0),
    doctorId: null as number | null,
    patientId: null as number | null,
    recordId: null as number | null,
    limit: 250,
  })
  const [phoneInput, setPhoneInput] = useState('')
  const [appliedPhone, setAppliedPhone] = useState('')
  const [codesPhoneFilter, setCodesPhoneFilter] = useState('')
  const [activitySearchInput, setActivitySearchInput] = useState('')
  const [activityAppliedSearch, setActivityAppliedSearch] = useState('')
  const [activityDaysInput, setActivityDaysInput] = useState('30')
  const [activityAppliedDays, setActivityAppliedDays] = useState(30)
  const [activityEventType, setActivityEventType] = useState('')
  const [activityAppliedEventType, setActivityAppliedEventType] = useState('')
  const [activityHideVerificationScript, setActivityHideVerificationScript] = useState(true)
  const [activityAppliedHideVerificationScript, setActivityAppliedHideVerificationScript] = useState(true)
  const [issuedSecretLink, setIssuedSecretLink] = useState<{ url: string; targetName?: string; expiresAt?: string } | null>(null)
  const activeNavigation = useMemo(() => navigation.find((item) => item.key === currentView) ?? navigation[0], [currentView, navigation])
  const canIssueSecretLinks = session.user.permissions.includes('secret_login') || session.user.permissions.includes('users')
  const autoOpenSecretLinks = session.user.permissions.includes('users')

  useEffect(() => {
    void loadCurrentSection(currentSection)
  }, [bootstrap.apiBase, currentSection, session.accessToken, appliedSearch, appliedPhone, page, medicalCardsQuery, activityAppliedSearch, activityAppliedDays, activityAppliedEventType, activityAppliedHideVerificationScript])

  useEffect(() => {
    if (currentSection !== 'list' || selectedPatientId === null) {
      return
    }

    setWorkspaceLoading(true)
    setWorkspaceError(null)
    void staffApi.getPatientWorkspace(bootstrap, session, selectedPatientId)
      .then((payload) => setPatientWorkspace(payload))
      .catch((reason: unknown) => {
        setWorkspaceError(reason instanceof Error ? reason.message : 'Не удалось загрузить карточку пациента.')
      })
      .finally(() => {
        setWorkspaceLoading(false)
      })
  }, [bootstrap, currentSection, selectedPatientId, session])

  async function loadCurrentSection(section: PatientSectionKey, force = false) {
    setLoadingMap((previous) => ({ ...previous, [section]: true }))
    setErrorMap((previous) => ({ ...previous, [section]: undefined }))

    try {
      if (section === 'list' && (force || !patientsList || patientsList.filters.search !== appliedSearch || patientsList.filters.page !== page)) {
        setPatientsList(await staffApi.getPatientsList(bootstrap, session, { page, perPage: 20, search: appliedSearch }))
      }

      if (
        section === 'medical-cards'
        && (
          force
          || !medicalCards
          || medicalCards.filters.from_date !== medicalCardsQuery.fromDate
          || medicalCards.filters.to_date !== medicalCardsQuery.toDate
          || (medicalCards.filters.doctor_id ?? null) !== medicalCardsQuery.doctorId
          || (medicalCards.filters.patient_id ?? null) !== medicalCardsQuery.patientId
          || (medicalCards.filters.record_id ?? null) !== medicalCardsQuery.recordId
        )
      ) {
        setMedicalCards(await staffApi.getPatientMedicalCards(bootstrap, session, medicalCardsQuery))
      }

      if (section === 'comments' && (force || !comments)) {
        setComments(await staffApi.getPatientComments(bootstrap, session))
      }

      if (section === 'segments' && (force || !segments)) {
        setSegments(await staffApi.getPatientSegments(bootstrap, session))
      }

      if (section === 'verification-codes' && (force || !verificationCodes || codesPhoneFilter !== appliedPhone)) {
        const payload = await staffApi.getPatientVerificationCodes(bootstrap, session, appliedPhone || undefined)
        setVerificationCodes(payload)
        setCodesPhoneFilter(appliedPhone)
      }

      if (
        section === 'activity'
        && (
          force
          || !activity
          || activity.filters.days !== activityAppliedDays
          || activity.filters.search !== activityAppliedSearch
          || (activity.filters.event_type ?? '') !== activityAppliedEventType
          || (activity.filters.exclude_verification_script ?? false) !== activityAppliedHideVerificationScript
        )
      ) {
        setActivity(await staffApi.getPatientActivity(bootstrap, session, {
          days: activityAppliedDays,
          search: activityAppliedSearch,
          eventType: activityAppliedEventType,
          limit: 200,
          excludeVerificationScript: activityAppliedHideVerificationScript,
        }))
      }
    } catch (reason) {
      setErrorMap((previous) => ({
        ...previous,
        [section]: reason instanceof Error ? reason.message : 'Не удалось загрузить раздел пациентов.',
      }))
    } finally {
      setLoadingMap((previous) => ({ ...previous, [section]: false }))
    }
  }

  async function handleIssuePatientSecretLink(patientId: number) {
    try {
      const link = await staffApi.createSecretLoginLink(bootstrap, session, {
        targetRole: 'patient',
        targetUserId: patientId,
        ttlMinutes: 15,
      })
      try {
        await navigator.clipboard.writeText(link.url)
      } catch {
        // ignore clipboard errors
      }
      if (autoOpenSecretLinks) {
        window.open(link.url, '_blank', 'noopener,noreferrer')
      } else {
        setIssuedSecretLink({
          url: link.url,
          targetName: link.targetDisplayName,
          expiresAt: link.expiresAt,
        })
      }
    } catch (reason) {
      const message = reason instanceof Error ? reason.message : 'Не удалось сформировать secret-ссылку.'
      setWorkspaceError(message)
    }
  }

  function renderCurrentSection() {
    if (currentSection === 'list') {
      return (
        <div className="grid gap-4 xl:grid-cols-[minmax(0,1.2fr)_minmax(380px,0.85fr)]">
          <PatientListSection
            data={patientsList}
            loading={loadingMap.list}
            error={errorMap.list ?? null}
            searchValue={searchInput}
            selectedPatientId={selectedPatientId}
            onSearchChange={setSearchInput}
            onSearchSubmit={() => {
              setPage(1)
              setAppliedSearch(searchInput.trim())
            }}
            onSelectPatient={(patientId) => {
              setSelectedPatientId(patientId)
            }}
            onRetry={() => void loadCurrentSection('list', true)}
            onPrevPage={() => setPage((current) => Math.max(1, current - 1))}
            onNextPage={() => setPage((current) => current + 1)}
          />
          <div className="grid min-w-0 gap-4">
            <PatientWorkspaceDetailSection
              data={patientWorkspace}
              loading={workspaceLoading}
              error={workspaceError}
              canIssueSecretLinks={canIssueSecretLinks}
              onIssuePatientSecretLink={(patientId) => void handleIssuePatientSecretLink(patientId)}
              onRetry={() => {
                if (selectedPatientId === null) {
                  return
                }

                setWorkspaceLoading(true)
                setWorkspaceError(null)
                void staffApi.getPatientWorkspace(bootstrap, session, selectedPatientId)
                  .then((payload) => setPatientWorkspace(payload))
                  .catch((reason: unknown) => {
                    setWorkspaceError(reason instanceof Error ? reason.message : 'Не удалось загрузить карточку пациента.')
                  })
                  .finally(() => {
                    setWorkspaceLoading(false)
                  })
              }}
            />
            {selectedPatientId !== null && bootstrap.capabilities.supportsDiagnocat === true ? (
              <>
                <PatientDiagnocatSection
                  bootstrap={bootstrap}
                  session={session}
                  patientId={selectedPatientId}
                  enabled
                />
                <PlanDiagnosticsBetaCardSection
                  bootstrap={bootstrap}
                  session={session}
                  lkPatientId={selectedPatientId}
                  staffUserId={String(session.user.id)}
                  enabled
                />
                <DiagnocatPatientCardWizard
                  bootstrap={bootstrap}
                  session={session}
                  lkPatientId={selectedPatientId}
                  staffUserId={String(session.user.id)}
                  enabled
                />
              </>
            ) : null}
          </div>
        </div>
      )
    }

    if (currentSection === 'medical-cards') {
      return (
        <PatientMedicalCardsSection
          data={medicalCards}
          loading={loadingMap['medical-cards']}
          error={errorMap['medical-cards'] ?? null}
          filterDraft={medicalCardsDraft}
          onFilterDraftChange={(patch) => setMedicalCardsDraft((current) => ({ ...current, ...patch }))}
          onApplyFilters={() => {
            setMedicalCardsQuery((current) => ({
              ...current,
              fromDate: medicalCardsDraft.fromDate,
              toDate: medicalCardsDraft.toDate,
              doctorId: medicalCardsDraft.doctorId ? Number(medicalCardsDraft.doctorId) : null,
              patientId: null,
              recordId: null,
            }))
          }}
          onResetFilters={() => {
            const resetDraft = {
              fromDate: relativeIsoDate(-30),
              toDate: relativeIsoDate(0),
              doctorId: '',
            }
            setMedicalCardsDraft(resetDraft)
            setMedicalCardsQuery({
              fromDate: resetDraft.fromDate,
              toDate: resetDraft.toDate,
              doctorId: null,
              patientId: null,
              recordId: null,
              limit: 250,
            })
          }}
          onSelectPatient={(patientId) => setMedicalCardsQuery((current) => ({ ...current, patientId, recordId: null }))}
          onSelectRecord={(recordId, patientId) => setMedicalCardsQuery((current) => ({ ...current, patientId, recordId }))}
          onClearSelection={() => setMedicalCardsQuery((current) => ({ ...current, patientId: null, recordId: null }))}
          onExport={() => void (async () => {
            const blob = await staffApi.downloadPatientMedicalCardsCsv(bootstrap, session, medicalCardsQuery)
            const url = window.URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = url
            link.download = 'medical_cards.csv'
            document.body.appendChild(link)
            link.click()
            link.remove()
            window.URL.revokeObjectURL(url)
          })()}
          onRetry={() => void loadCurrentSection('medical-cards', true)}
        />
      )
    }

    if (currentSection === 'comments') {
      return (
        <PatientCommentsSection
          data={comments}
          loading={loadingMap.comments}
          error={errorMap.comments ?? null}
          onRetry={() => void loadCurrentSection('comments', true)}
        />
      )
    }

    if (currentSection === 'segments') {
      return (
        <PatientSegmentsSection
          data={segments}
          loading={loadingMap.segments}
          error={errorMap.segments ?? null}
          onRetry={() => void loadCurrentSection('segments', true)}
        />
      )
    }

    if (currentSection === 'activity') {
      return (
        <PatientActivitySection
          data={activity}
          loading={loadingMap.activity}
          error={errorMap.activity ?? null}
          searchValue={activitySearchInput}
          daysValue={activityDaysInput}
          eventTypeValue={activityEventType}
          hideVerificationScript={activityHideVerificationScript}
          onSearchChange={setActivitySearchInput}
          onDaysChange={setActivityDaysInput}
          onEventTypeChange={setActivityEventType}
          onHideVerificationScriptChange={setActivityHideVerificationScript}
          onFilterSubmit={() => {
            setActivity(null)
            setActivityAppliedSearch(activitySearchInput.trim())
            const days = Number.parseInt(activityDaysInput, 10)
            setActivityAppliedDays(Number.isFinite(days) ? Math.max(1, Math.min(365, days)) : 30)
            setActivityAppliedEventType(activityEventType)
            setActivityAppliedHideVerificationScript(activityHideVerificationScript)
          }}
          onRetry={() => void loadCurrentSection('activity', true)}
        />
      )
    }

    return (
      <PatientVerificationCodesSection
        data={verificationCodes}
        loading={loadingMap['verification-codes']}
        error={errorMap['verification-codes'] ?? null}
        phoneValue={phoneInput}
        onPhoneChange={setPhoneInput}
        onFilterSubmit={() => setAppliedPhone(phoneInput.trim())}
        onRetry={() => void loadCurrentSection('verification-codes', true)}
      />
    )
  }

  return (
    <StaffShell
      title="Пациенты и служебные потоки"
      subtitle="Ветка patients теперь ведет к drill-down workspace в стиле UlybkaPRO: список, карточка пациента, медкарты, комментарии, сегментация и verification codes."
      navigation={navigation}
      currentView={currentView}
      onNavigate={onNavigate}
      session={session}
      bootstrapTenant={bootstrap.tenant}
      onRefresh={() => void loadCurrentSection(currentSection, true)}
      onLogout={() => void onLogout()}
    >
      <section className="grid gap-4 rounded-[28px] border border-slate-200/70 bg-white p-6 shadow-[0_18px_42px_rgba(15,23,42,0.08)]">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="mb-2 text-xs font-bold uppercase tracking-[0.14em] text-sky-600">Patients workspace</p>
            <h2 className="text-xl font-semibold text-slate-900">{activeNavigation?.label ?? 'Пациенты'}</h2>
            <p className="mt-1 text-sm leading-6 text-slate-500">Внутри ветки patients секции собраны как отдельные рабочие поверхности, а не как плоский список legacy-эндпоинтов.</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2.5">
          {(Object.keys(sectionLabels) as PatientSectionKey[]).map((section) => (
            <button
              key={section}
              type="button"
              className={`rounded-[18px] border px-4 py-3 text-sm font-medium transition ${currentSection === section ? 'border-transparent bg-[linear-gradient(135deg,#0b74de_0%,#0099ff_100%)] text-white' : 'border-sky-100 bg-sky-50 text-sky-800'}`}
              onClick={() => setCurrentSection(section)}
            >
              {sectionLabels[section]}
            </button>
          ))}
        </div>
      </section>

      {renderCurrentSection()}
      {issuedSecretLink ? (
        <SecretLoginLinkDialog
          url={issuedSecretLink.url}
          targetName={issuedSecretLink.targetName}
          expiresAt={issuedSecretLink.expiresAt}
          onClose={() => setIssuedSecretLink(null)}
        />
      ) : null}
    </StaffShell>
  )
}
